import { Component, EventEmitter, Input, OnChanges, Output, ChangeDetectorRef, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { ActivatedRoute, Router, QueryParamsHandling } from '@angular/router';
import { AuthService } from '../../auth';
import { PaginatedDeliveries } from '../paginated-deliveries.model';
import { MediaService } from '../../content';
import { AvailableLanguage, LanguageService } from '../../language';
import { WindowService, FormCachingService, DataProcessingService } from '../../utilities';
import { Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { switchMap, catchError, tap, distinctUntilChanged, startWith, skip, map, filter } from 'rxjs/operators';
import { of } from 'rxjs';
import { ContributionsService } from '../contributions.service';
import { LinksService } from '@frontend/shared';
import { Link } from '@frontend/core';
import { Delivery } from '../delivery.model';
import { ContributionsFilters, ContributionsOptions, ContributionsQueryParams } from '../contributions-filters.types';
import { Culture, CultureService } from '../../cultures';


@Component({
  selector: 'multisite-contributions-home',
  templateUrl: './contributions-home.component.html',
  styleUrls: ['./contributions-home.component.scss'],

  standalone: false,
})
export class ContributionsHomeComponent implements OnChanges, OnInit, OnDestroy {

  cultureService = inject(CultureService);
  private dataProcessingService = inject(DataProcessingService);
  nationalCultures = signal<Culture[]>([]);
  loadingObject: {[key:string]:boolean} = {}; // .topic .course .personalisedData
  private subscriptions: Subscription[] = [];
  interculturalistRoleAlertComments: boolean = false;
  error = null;
  paginatedDeliveries : PaginatedDeliveries;
  activeLanguageObject : AvailableLanguage;
  thumbnailTransformations: string = 'ar_16:9,w_700/';
  cloudinary_base_url : string;
  links : Link[] = [];

  // Signals for reactive state management
  private queryParamsSignal = signal<ContributionsQueryParams>({
    filters: {},
    options: { sort_by: 'started_at', sort_order: 'asc', items_per_page: 10, page: 1 }
  });
  
  queryParams = this.queryParamsSignal.asReadonly();
  
  // We'll subscribe directly to route.queryParams instead of using a manual trigger
  
  // Form caching key
  private readonly CACHE_KEY = 'contributions_filters';
  
  constructor(
    private route : ActivatedRoute,
    private router : Router,
    private authService : AuthService,
    private mediaService : MediaService,
    private languageService : LanguageService,
    private windowService : WindowService,
    private contributionsService : ContributionsService,
    private linksService : LinksService,
    private formCachingService: FormCachingService,
  ) {
    this.cloudinary_base_url = this.mediaService.cloudinary_base_url;
    
    // Don't set up reactive data loading stream here - wait for ngOnInit
  }

  private setupDataStream(initialQueryParams: ContributionsQueryParams): void {
    // Set initial state
    this.queryParamsSignal.set(initialQueryParams);
    this.activeLanguageObject = this.languageService.activeLanguageObjectSynchronously;
    
    // Load initial data
    this.loadInitialData(initialQueryParams);
    
    // Set up reactive stream for subsequent changes (skip initial emission)
    const dataSubscription = combineLatest([
      this.route.queryParams.pipe(skip(1)), // Skip initial emission to avoid duplicate load
      this.languageService.activeLanguageObject
    ]).pipe(
      distinctUntilChanged(([prevParams, prevLang], [currParams, currLang]) => 
        JSON.stringify(prevParams) === JSON.stringify(currParams) && 
        prevLang?.languageKey === currLang?.languageKey
      ),
      tap(() => this.loadingObject['initialData'] = true),
      switchMap(([params, language]) => {
        const queryParams = this.contributionsService.parseUrlQueryParams(params);
        this.queryParamsSignal.set(queryParams);
        this.activeLanguageObject = language;
        return this.contributionsService.getFilteredPaginatedDeliveries(queryParams, false).pipe(
          catchError(error => {
            this.error = error;
            this.loadingObject['initialData'] = false;
            return of(null);
          })
        );
      }),
      tap(() => this.loadingObject['initialData'] = false)
    ).subscribe(reportData => {
      if (reportData) {
        this.paginatedDeliveries = reportData.data as PaginatedDeliveries;
        this.error = null;
      }
    });
    
    this.subscriptions.push(dataSubscription);
  }

  private getCultures(): void {
    this.loadingObject['cultures'] = true;
    const cachedCultures = this.cultureService.cultures.filter(culture => culture.type === 'national' && culture.category === 'geographic');
    const culturesSubscription = this.cultureService.cultures$.pipe(
      filter(cultures => cultures.some(culture => culture.type === 'national' && culture.category === 'geographic')),
      map(cultures => cultures)
    ).subscribe((cultures :  Culture[]) => {
      this.nationalCultures.set(cultures);
      this.loadingObject['cultures'] = false;
    });
    this.subscriptions.push(culturesSubscription);
    if(cachedCultures.length === 0){
      const fetchFreshCulturesSub = this.cultureService.getCultures('geographic','national',false).subscribe(()=>{}, e=>this.loadingObject['cultures'] = false);
      this.subscriptions.push(fetchFreshCulturesSub);
    }
  }

  private loadInitialData(queryParams: ContributionsQueryParams): void {
    this.loadingObject['initialData'] = true;
    const initialDataSubscription = this.contributionsService.getFilteredPaginatedDeliveries(queryParams, false).pipe(
      catchError(error => {
        this.error = error;
        this.loadingObject['initialData'] = false;
        return of(null);
      }),
      tap(() => this.loadingObject['initialData'] = false)
    ).subscribe(reportData => {
      if (reportData) {
        this.paginatedDeliveries = reportData.data as PaginatedDeliveries;
        this.error = null;
      }
    });
    
    this.subscriptions.push(initialDataSubscription);
  }

  getFallbackFlagUrl (){
    return this.mediaService.fallback_flag_round_url;
  }
  gotoDelivery(delivery : Delivery){
    if(!delivery){return;}
    // A feature review taken from a community input works on its own screen — the Ideas plan, decision 3.32.
    if (delivery.input?.kind === 'feature_review'){
      this.router.navigate(['/contributions/inputs', delivery.input.slug, 'review']);
      return;
    }
    if (delivery.input?.kind === 'video_recording'){
      // Staff may give a member several videos from one input, so the screen is told which.
      this.router.navigate(['/contributions/inputs', delivery.input.slug, 'video'], { queryParams: { delivery: delivery.id } });
      return;
    }
    if (delivery.task.slug == 'insight-subject-written-short'){
      const queryParams = {};
      if (delivery.source_lang) {
        queryParams['source_lang'] = delivery.source_lang;
      }
      if (delivery.target_lang) {
        queryParams['target_lang'] = delivery.target_lang;
      }
      this.router.navigate(['/contributions/insights/create/', delivery.insight?.slug], { queryParams });
    } else if (delivery.task.slug == 'difference-vote-cco'){

      this.router.navigate(['/contributions/differences/vote/', delivery.votes?.[0]?.id]);
    }

  }

  isAnythingLoading(){
    for (let something in this.loadingObject){
      if (this.loadingObject[something]){
        return true;
      }
    }
    return false;
  }
  hasRole (roleName:string){
    return this.authService.checkRole(roleName); // for example "Admin"
  }
  getFlagUrlFromHash(hash:string,transformations : string = ''){
    return this.mediaService.getFlagUrlFromHash(hash,'',true,'.png');
  }

  doGetPaginatedDeliveries(page:number = 1, freshFromServer : boolean = false){
    this.updatePageAndNavigate(page);
  }

  private updatePageAndNavigate(page: number) {
    const newQueryParams = {
      ...this.queryParams(),
      options: { ...this.queryParams().options, page }
    };
    this.updateStateAndNavigate(newQueryParams);
  }

  // The filters component also emits its current state when it starts, so an unchanged emission is ignored
  // rather than treated as a change that resets the page.
  onFiltersChange(filters: ContributionsFilters) {
    const current = this.queryParams();
    if (this.dataProcessingService.matchTwoComplexObjects(current.filters, filters)) {
      return;
    }
    const newQueryParams = {
      ...current,
      filters,
      options: { ...current.options, page: 1 } // Different filters give a different list, so start at its first page
    };
    this.updateStateAndNavigate(newQueryParams);
  }

  onOptionsChange(options: ContributionsOptions) {
    const currentOptions = this.queryParams().options;
    // Re-sorting keeps the reader on their page; a new page size may leave that page out of range
    const page = options.items_per_page === currentOptions.items_per_page ? currentOptions.page : 1;
    const newOptions = { ...options, page };
    if (this.dataProcessingService.matchTwoComplexObjects(currentOptions, newOptions)) {
      return;
    }
    const newQueryParams = {
      ...this.queryParams(),
      options: newOptions
    };
    this.updateStateAndNavigate(newQueryParams);
  }

  private updateStateAndNavigate(queryParams: ContributionsQueryParams) {
    // Cache the form data
    this.formCachingService.cacheFormData(queryParams, this.CACHE_KEY);
    
    // Navigate with new query params - this will trigger data loading via queryParams subscription
    this.navigateWithFilters(queryParams);
  }

  private navigateWithFilters(queryParams: ContributionsQueryParams) {
    const urlParams = this.contributionsService.formatUrlQueryParams(queryParams);
    this.router.navigate([], {
      queryParams: urlParams,
      queryParamsHandling: 'replace' as QueryParamsHandling
    });
  }

  private loadCachedFiltersOrQueryParams(): ContributionsQueryParams {
    const cachedData = this.formCachingService.getDataItem(this.CACHE_KEY);
    const currentQueryParams = this.route.snapshot.queryParams;
    
    if (Object.keys(currentQueryParams).length === 0 && cachedData) {
      // We have cached data but no URL params - restore cached data to URL
      this.navigateWithFilters(cachedData);
      return cachedData;
    } else {
      // Use current URL params (could be empty, which means defaults)
      return this.contributionsService.parseUrlQueryParams(currentQueryParams);
    }
  }

  getLinks(){
    let linksRequest = [
      {slug:'demo',class:'demo',topic:'demo',site:'demo',subdomain:'demo'},
      {slug:'demo',class:'demo',topic:'demo',site:'demo',subdomain:'demo'},
      {slug:'demo',class:'demo',topic:'demo',site:'demo',subdomain:'demo'},
    ]
    const linksSubscription = this.linksService.getDynamicLinks(linksRequest).subscribe(links =>{
      this.links = links;
    });
    this.subscriptions.push(linksSubscription);
  }
  initialise (){
    this.getLinks();
    // Data loading will be triggered by the initial loadDataTrigger value set in constructor
  }

  ngOnInit() {
    this.windowService.goToTop();
    
    // Determine final query params (from URL, cache, or defaults) and handle any necessary navigation
    const initialQueryParams = this.loadCachedFiltersOrQueryParams();
    
    // Set up data loading with the determined initial params
    this.setupDataStream(initialQueryParams);
    
    // Get links separately (doesn't need to be part of data loading)
    this.getLinks();
    this.getCultures();
  }

  ngOnChanges(changesObject) : void {

  }
  
  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }


}
